import { Fragment, useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { api } from '../../../services/api'
import { realCellText, realStatusColor, realStatusLabel, sumReal, sumRealTotal } from '../../../utils/billingRedmine'
import type { RedmineRowFields, RedmineTableFields } from '../../../utils/billingRedmine'
import { Spinner, LoadingContainer } from '../../../components/shared/StyledComponents'

const Section = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`

const SectionTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`

const Controls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`

const Select = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const Button = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// Points admins at the "Equipo" tab of Imputación, where who-is-behind lives
// now; this table keeps showing the plan, not the follow-up.
const TeamImputacionLink = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`

const InfoBox = styled.div`
  background: #E3F2FD;
  border-left: 4px solid #1976D2;
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const SummaryCard = styled.div`
  background: linear-gradient(135deg, #E3F2FD, #fff);
  border: 1px solid #90CAF9;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
`

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const TableWrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
`

const SummaryTableWrapper = styled(TableWrapper)`
    margin-top: ${({ theme }) => theme.spacing.lg};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  min-width: 800px;
`

const SummaryTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    min-width: 520px;
`

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.secondary};
  position: sticky;
  top: 0;
  z-index: 10;
`

const SummaryHead = styled.thead`
    background: ${({ theme }) => theme.colors.secondary};
`

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.sm};
  text-align: left;
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const SummaryTh = styled.th`
    padding: ${({ theme }) => theme.spacing.sm};
    text-align: left;
    font-weight: 600;
    border: 1px solid ${({ theme }) => theme.colors.border};
    white-space: nowrap;
`

const Tbody = styled.tbody``

const Tr = styled.tr<{ $isDefault?: boolean; $isUserGroup?: boolean }>`
  background: ${({ $isDefault, $isUserGroup }) =>
        $isUserGroup ? '#F5F5F5' :
            $isDefault ? '#FFF8E1' : 'white'};

  ${({ $isUserGroup }) => $isUserGroup && `
    font-weight: 600;
    border-top: 2px solid #ddd;
  `}

  &:hover {
    background: ${({ theme }) => theme.colors.primaryLight};
  }
`

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.xs};

  &:first-child {
    text-align: left;
    font-weight: 500;
  }
`

const SummaryTd = styled.td`
    padding: ${({ theme }) => theme.spacing.sm};
    border: 1px solid ${({ theme }) => theme.colors.border};
    text-align: left;
`

const SummaryTitle = styled.h3`
    margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.sm} 0;
    font-size: ${({ theme }) => theme.fontSizes.md};
    color: ${({ theme }) => theme.colors.primary};
`

const UserLabel = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const ProjectLabel = styled.span<{ $isDefault?: boolean }>`
  color: ${({ $isDefault, theme }) => $isDefault ? '#F57C00' : theme.colors.text};
  font-style: ${({ $isDefault }) => $isDefault ? 'italic' : 'normal'};
  padding-left: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const SummaryProjectLabel = styled.span<{ $isDefault?: boolean }>`
    color: ${({ $isDefault, theme }) => $isDefault ? '#F57C00' : theme.colors.text};
    font-style: ${({ $isDefault }) => $isDefault ? 'italic' : 'normal'};
    font-weight: 600;
`

const RealHours = styled.div<{ $status?: string | null }>`
  font-size: 0.68rem;
  line-height: 1.1;
  color: ${({ $status }) => realStatusColor($status as any)};
`

const RedmineTag = styled.span`
  display: inline-block;
  margin-left: 4px;
  padding: 0 5px;
  border-radius: 8px;
  font-size: 0.62rem;
  font-weight: 700;
  background: #E3F2FD;
  color: #1565C0;
  vertical-align: middle;
`

const RedmineNote = styled.div`
  font-size: 0.75rem;
  color: #555;
  margin: 6px 0 10px;
  padding: 6px 10px;
  border-left: 3px solid #90CAF9;
  background: #F5F9FF;
  border-radius: 4px;
`

const HoursCell = styled.div<{ $hours: number; $overload?: boolean }>`
  color: ${({ $hours, $overload, theme }) =>
        $overload ? '#D32F2F' :
            $hours === 0 ? theme.colors.textMuted :
                $hours < 4 ? '#0288D1' :
                    $hours < 7 ? '#388E3C' :
                        '#1976D2'
    };
  font-weight: ${({ $hours, $overload }) => ($hours > 0 || $overload) ? 600 : 400};
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textMuted};
`

interface ProjectAllocationRow extends RedmineRowFields {
    user_id: number
    user_name: string
    opportunity_id?: number
    project_name: string
    redmine_code?: string
    daily_hours: { [date: string]: number }
    total_hours: number
    capacity_pct?: number
    is_default_project: boolean
    has_overload?: boolean
}

interface MonthlyAllocationTable extends RedmineTableFields {
    year: number
    month: number
    working_days: string[]
    allocations: ProjectAllocationRow[]
    overloaded_users?: string[]
}

const AlertBox = styled.div`
  background: #FFEBEE;
  border-left: 4px solid #D32F2F;
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: #B71C1C;
  font-weight: 600;
`

interface BillingStatus {
    redmine_code: string
    total_budget: number | null
    hours_used: number | null
    pct_consumed: number | null
    /** 'time_material': billed by the hours logged in Redmine (no bag of hours). */
    billing_mode?: string | null
    hours_logged_month?: number | null
    alert: {
        type: string
        time_progress_pct?: number
        hours_progress_pct?: number
        projected_total_hours?: number
        /** The node that raised it: the project or one of its subprojects */
        redmine_code?: string
        message: string
    } | null
    /** Redmine could not be read for this opportunity (figures unknown, not zero) */
    redmine_error?: string | null
}

const BillingBadge = styled.span<{ $alert?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 8px;
  background: ${({ $alert }) => $alert ? '#FFEBEE' : '#E8F5E9'};
  color: ${({ $alert }) => $alert ? '#C62828' : '#2E7D32'};
  border: 1px solid ${({ $alert }) => $alert ? '#EF9A9A' : '#A5D6A7'};
`

const ProjectionAlert = styled.div`
  font-size: 0.7rem;
  color: #E65100;
  margin-top: 2px;
`

export const TeamBillingSection = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [tableData, setTableData] = useState<MonthlyAllocationTable | null>(null)
    const [billingStatuses, setBillingStatuses] = useState<Record<string, BillingStatus>>({})
    const [billingMessage, setBillingMessage] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    const currentDate = new Date()
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear())
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1)

    useEffect(() => {
        fetchTeamAllocations()
    }, [selectedYear, selectedMonth])

    const fetchTeamAllocations = async () => {
        try {
            setLoading(true)
            const [response, billingRes] = await Promise.all([
                api.billing.getAllocations(selectedYear, selectedMonth),
                // Same month as the table: the forecast column used to show the
                // current month's projection next to another month's plan.
                api.billing.getOpportunityStatus(selectedYear, selectedMonth).catch(() => ({
                    data: { statuses: {}, redmine_available: false, redmine_message: 'Previsión de Redmine no disponible' },
                })),
            ])
            setTableData(response.data)
            setBillingStatuses(billingRes.data?.statuses || {})
            setBillingMessage(billingRes.data?.redmine_available === false ? (billingRes.data?.redmine_message || 'Redmine no disponible') : null)
            setError(null)
        } catch (err: any) {
            console.error('[TeamBilling] Error fetching allocations:', err)
            setError(err.response?.data?.detail || 'Error al cargar la tabla de imputaciones del equipo')
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr)
        const day = date.getDate()
        return `${day}`
    }

    const getTotalHours = (): number => {
        if (!tableData) return 0
        return tableData.allocations.reduce((sum, row) => sum + row.total_hours, 0)
    }

    const getUniqueUsers = (): number => {
        if (!tableData) return 0
        const userIds = new Set(tableData.allocations.map(row => row.user_id))
        return userIds.size
    }

    const getActiveProjects = (): number => {
        if (!tableData) return 0
        const projects = new Set(
            tableData.allocations
                .filter(row => !row.is_default_project)
                .map(row => row.project_name)
        )
        return projects.size
    }

    // Group allocations by user
    const groupedByUser = () => {
        if (!tableData) return []

        const userGroups = new Map<number, ProjectAllocationRow[]>()

        tableData.allocations.forEach(row => {
            if (!userGroups.has(row.user_id)) {
                userGroups.set(row.user_id, [])
            }
            userGroups.get(row.user_id)!.push(row)
        })

        return Array.from(userGroups.entries()).map(([userId, rows]) => ({
            userId,
            userName: rows[0].user_name,
            rows
        }))
    }

    const projectSummary = () => {
        if (!tableData) return []

        const summaryMap = new Map<
            string,
            {
                opportunity_id?: number
                project_name: string
                is_default_project: boolean
                total_hours: number
                user_ids: Set<number>
            }
        >()

        tableData.allocations.forEach((row) => {
            const key = row.is_default_project
                ? `default:${row.project_name}`
                : `opp:${row.opportunity_id ?? row.project_name}`

            if (!summaryMap.has(key)) {
                summaryMap.set(key, {
                    opportunity_id: row.opportunity_id,
                    project_name: row.project_name,
                    is_default_project: row.is_default_project,
                    total_hours: 0,
                    user_ids: new Set<number>(),
                })
            }

            const entry = summaryMap.get(key)
            if (entry) {
                entry.total_hours += row.total_hours
                entry.user_ids.add(row.user_id)
            }
        })

        return Array.from(summaryMap.values()).sort((a, b) => b.total_hours - a.total_hours)
    }

    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ]

    const years = Array.from({ length: 3 }, (_, i) => currentDate.getFullYear() - 1 + i)

    return (
        <Section>
            <SectionHeader>
                <SectionTitle>👥 Imputaciones del Equipo</SectionTitle>
                <Controls>
                    <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    >
                        {monthNames.map((name, index) => (
                            <option key={index} value={index + 1}>{name}</option>
                        ))}
                    </Select>
                    <Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </Select>
                    <Button onClick={fetchTeamAllocations}>🔄 Actualizar</Button>
                </Controls>
            </SectionHeader>

            <TeamImputacionLink>
                <span>Estado de la imputación del equipo</span>
                <Link to="/imputacion?tab=equipo">→ Ver en Imputación</Link>
            </TeamImputacionLink>

            <InfoBox>
                💡 Esta tabla muestra cómo cada miembro del equipo debe distribuir sus horas entre proyectos.
                Cada usuario tiene al menos una línea de imputación (proyecto activo o proyecto por defecto).
            </InfoBox>
            {tableData && (
                <RedmineNote>
                    {tableData.redmine_available ? (
                        <>
                            🔗 Debajo de cada hora planificada aparece, en pequeño, la hora <strong>real imputada en Redmine</strong>
                            {tableData.as_of ? ` (hasta ${tableData.as_of})` : ''}: verde al día, naranja por debajo, rojo por encima, azul horas en Redmine sin planificar.
                            {tableData.unmatched_users && tableData.unmatched_users.length > 0 && (
                                <> Sin usuario Redmine identificado: {tableData.unmatched_users.join(', ')}.</>
                            )}
                        </>
                    ) : (
                        <>⚠️ Horas reales de Redmine no disponibles{tableData.redmine_message ? `: ${tableData.redmine_message}` : ''}.</>
                    )}
                </RedmineNote>
            )}

            {loading && (
                <LoadingContainer>
                    <Spinner />
                    <p>Cargando imputaciones del equipo...</p>
                </LoadingContainer>
            )}

            {!loading && error && <EmptyState>{error}</EmptyState>}

            {!loading && !error && tableData && (
                <>
                    {tableData.working_days.length === 0 ? (
                        <EmptyState>
                            <p>No hay días laborables para {monthNames[selectedMonth - 1]} {selectedYear}.</p>
                        </EmptyState>
                    ) : (
                        <>
                            <SummaryCards>
                                <SummaryCard>
                                    <SummaryValue>{getTotalHours().toFixed(1)}h</SummaryValue>
                                    <SummaryLabel>Total Equipo</SummaryLabel>
                                </SummaryCard>
                                <SummaryCard>
                                    <SummaryValue>{getUniqueUsers()}</SummaryValue>
                                    <SummaryLabel>Miembros</SummaryLabel>
                                </SummaryCard>
                                <SummaryCard>
                                    <SummaryValue>{getActiveProjects()}</SummaryValue>
                                    <SummaryLabel>Proyectos Activos</SummaryLabel>
                                </SummaryCard>
                                <SummaryCard>
                                    <SummaryValue>{tableData.working_days.length}</SummaryValue>
                                    <SummaryLabel>Días Laborables</SummaryLabel>
                                </SummaryCard>
                            </SummaryCards>

                            {tableData.overloaded_users && tableData.overloaded_users.length > 0 && (
                                <AlertBox>
                                    ⚠️ Conflicto de carga: {tableData.overloaded_users.join(', ')} tienen asignación superior al 100%.
                                    Las horas se han redistribuido proporcionalmente. Revisa las asignaciones.
                                </AlertBox>
                            )}

                            <TableWrapper>
                                <Table>
                                    <Thead>
                                        <tr>
                                            <Th>Usuario / Proyecto</Th>
                                            {tableData.working_days.map(day => (
                                                <Th key={day}>{formatDate(day)}</Th>
                                            ))}
                                            <Th>Total</Th>
                                        </tr>
                                    </Thead>
                                    <Tbody>
                                        {groupedByUser().length === 0 ? (
                                            <Tr>
                                                <Td colSpan={tableData.working_days.length + 2} style={{ textAlign: 'center', fontStyle: 'italic', color: '#999' }}>
                                                    No hay datos de imputación para este mes.
                                                </Td>
                                            </Tr>
                                        ) : (
                                            groupedByUser().map(({ userId, userName, rows }) => {
                                                const userOverloaded = rows.some(r => r.has_overload)
                                                return (
                                                    <Fragment key={`user-${userId}`}>
                                                        {/* User header row */}
                                                        <Tr $isUserGroup>
                                                            <Td>
                                                                <UserLabel>
                                                                    {userName}
                                                                    {userOverloaded && <span style={{ color: '#D32F2F', marginLeft: 6 }}>⚠️ Sobrecarga</span>}
                                                                </UserLabel>
                                                            </Td>
                                                            {tableData.working_days.map(day => (
                                                                <Td key={day}>
                                                                    <HoursCell $hours={
                                                                        rows.reduce((sum, row) => sum + (row.daily_hours[day] || 0), 0)
                                                                    }>
                                                                        {rows.reduce((sum, row) => sum + (row.daily_hours[day] || 0), 0).toFixed(2)}
                                                                    </HoursCell>
                                                                    {tableData.redmine_available && sumReal(rows, day) > 0 && (
                                                                        <RealHours title="Horas reales en Redmine">{sumReal(rows, day).toFixed(2)}</RealHours>
                                                                    )}
                                                                </Td>
                                                            ))}
                                                            <Td>
                                                                <strong>{rows.reduce((sum, row) => sum + row.total_hours, 0).toFixed(1)}h</strong>
                                                                {tableData.redmine_available && sumRealTotal(rows) > 0 && (
                                                                    <RealHours title="Horas reales en Redmine">{sumRealTotal(rows).toFixed(1)}h Redmine</RealHours>
                                                                )}
                                                            </Td>
                                                        </Tr>
                                                        {/* Project rows for this user */}
                                                        {rows.map((row, index) => (
                                                            <Tr key={`${userId}-${index}`} $isDefault={row.is_default_project}>
                                                                <Td>
                                                                    {row.opportunity_id && !row.is_default_project ? (
                                                                        <a
                                                                            href="#"
                                                                            onClick={(e) => {
                                                                                e.preventDefault()
                                                                                navigate('/oportunidades', { state: { openOpportunityId: row.opportunity_id } })
                                                                            }}
                                                                            style={{ textDecoration: 'none' }}
                                                                        >
                                                                            <ProjectLabel $isDefault={false}>
                                                                                {row.project_name}
                                                                                {row.is_real_only && <RedmineTag>Redmine</RedmineTag>}
                                                                                {row.capacity_pct != null && ` (${row.capacity_pct}%)`}
                                                                                {row.has_overload && ' ⚠️'}
                                                                            </ProjectLabel>
                                                                        </a>
                                                                    ) : (
                                                                        <ProjectLabel $isDefault={row.is_default_project}>
                                                                            {row.project_name}
                                                                            {row.is_real_only && <RedmineTag>Redmine</RedmineTag>}
                                                                            {row.capacity_pct != null && ` (${row.capacity_pct}%)`}
                                                                        </ProjectLabel>
                                                                    )}
                                                                </Td>
                                                                {tableData.working_days.map(day => (
                                                                    <Td key={day}>
                                                                        <HoursCell $hours={row.daily_hours[day] || 0} $overload={row.has_overload}>
                                                                            {row.daily_hours[day] ? row.daily_hours[day].toFixed(2) : '-'}
                                                                        </HoursCell>
                                                                        {realCellText(row, day, tableData.as_of) !== '' && (
                                                                            <RealHours $status={row.real_status} title={realStatusLabel(row.real_status)}>{realCellText(row, day, tableData.as_of)}</RealHours>
                                                                        )}
                                                                    </Td>
                                                                ))}
                                                                <Td>
                                                                    <strong style={{ color: row.has_overload ? '#D32F2F' : undefined }}>
                                                                        {row.total_hours.toFixed(1)}h
                                                                    </strong>
                                                                    {row.real_available && row.total_real != null && (
                                                                        <RealHours $status={row.real_status} title={realStatusLabel(row.real_status)}>{row.total_real.toFixed(1)}h Redmine</RealHours>
                                                                    )}
                                                                </Td>
                                                            </Tr>
                                                        ))}
                                                    </Fragment>
                                                )
                                            })
                                        )}
                                    </Tbody>
                                </Table>
                            </TableWrapper>
                            <SummaryTitle>📌 Resumen por proyecto (fin de mes)</SummaryTitle>
                            {billingMessage && (
                                <RedmineNote>⚠️ Imputación Redmine no disponible: {billingMessage}</RedmineNote>
                            )}
                            <SummaryTableWrapper>
                                <SummaryTable>
                                    <SummaryHead>
                                        <tr>
                                            <SummaryTh>Proyecto</SummaryTh>
                                            <SummaryTh>Usuarios</SummaryTh>
                                            <SummaryTh>Total Mes</SummaryTh>
                                            <SummaryTh>Imputación Redmine</SummaryTh>
                                        </tr>
                                    </SummaryHead>
                                    <Tbody>
                                        {projectSummary().length === 0 ? (
                                            <Tr>
                                                <SummaryTd colSpan={4} style={{ textAlign: 'center', fontStyle: 'italic', color: '#999' }}>
                                                    No hay datos de imputación para este mes.
                                                </SummaryTd>
                                            </Tr>
                                        ) : (
                                            projectSummary().map((project) => {
                                                const bs = project.opportunity_id
                                                    ? billingStatuses[String(project.opportunity_id)]
                                                    : undefined
                                                return (
                                                    <Tr key={`${project.project_name}-${project.opportunity_id ?? 'default'}`} $isDefault={project.is_default_project}>
                                                        <SummaryTd>
                                                            {project.opportunity_id && !project.is_default_project ? (
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault()
                                                                        navigate('/oportunidades', { state: { openOpportunityId: project.opportunity_id } })
                                                                    }}
                                                                    style={{ textDecoration: 'none' }}
                                                                >
                                                                    <SummaryProjectLabel $isDefault={false}>
                                                                        {project.project_name}
                                                                    </SummaryProjectLabel>
                                                                </a>
                                                            ) : (
                                                                <SummaryProjectLabel $isDefault={project.is_default_project}>
                                                                    {project.project_name}
                                                                </SummaryProjectLabel>
                                                            )}
                                                        </SummaryTd>
                                                        <SummaryTd>{project.user_ids.size}</SummaryTd>
                                                        <SummaryTd>{project.total_hours.toFixed(1)}h</SummaryTd>
                                                        <SummaryTd>
                                                            {bs && (bs.pct_consumed != null || bs.alert || bs.redmine_error || bs.billing_mode === 'time_material') ? (
                                                                <div>
                                                                    {bs.billing_mode === 'time_material' && bs.pct_consumed == null && (
                                                                        <BillingBadge $alert={false} title="Time & Material: se factura por las horas imputadas en Redmine">
                                                                            T&M · {bs.hours_logged_month ?? 0}h este mes
                                                                        </BillingBadge>
                                                                    )}
                                                                    {bs.pct_consumed != null && (
                                                                        <BillingBadge $alert={!!bs.alert}>
                                                                            {bs.pct_consumed}% consumido
                                                                            ({bs.hours_used}h / {bs.total_budget}h)
                                                                            {bs.alert && ' ⚠️'}
                                                                        </BillingBadge>
                                                                    )}
                                                                    {bs.alert && (
                                                                        <ProjectionAlert>
                                                                            {bs.alert.message}
                                                                        </ProjectionAlert>
                                                                    )}
                                                                    {bs.redmine_error && (
                                                                        <ProjectionAlert title={bs.redmine_error}>
                                                                            Redmine no disponible
                                                                        </ProjectionAlert>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#999', fontSize: '0.8rem' }}>—</span>
                                                            )}
                                                        </SummaryTd>
                                                    </Tr>
                                                )
                                            })
                                        )}
                                    </Tbody>
                                </SummaryTable>
                            </SummaryTableWrapper>
                        </>
                    )}
                </>
            )}
        </Section>
    )
}
